import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import moment, { Moment } from "moment";

import CtaComponent from "../../../components/reusable-ui/cta";
import CustomDropdown from "../../../components/reusable-ui/custom-dropdown";

import { CTA_SIZE, CTA_TYPE, DATE_TIME_FORMATS } from "../../../data/constants";
import { convertMomentToString } from "../../../helpers/moment-helper";
import { RootState } from "../../../redux/store";
import { AGGREGATOR_TYPE } from "../../../data/outlet-status-constants";
import { Checkbox } from "@mui/material";
import { getAggregatorLogo } from "../../../helpers/accounting-helper";
import { cloneDeep, set } from "lodash";
import "./style.scss";
import DateRangePicker from "../../../components/reusable-ui/date-range-picker";

interface Props {
  onApplyCtaClick: any;
  setIsFileUploadModalOpen: any;
}

const TopActionBar: FC<Props> = (props: Props) => {
  const { onApplyCtaClick, setIsFileUploadModalOpen } = props;

  const [filterConfig, setFilterConfig] = useState<any>({
    aggregatorType: "",
    branchId: "",
    channelDefId: "",
    dateRange: {
      endDate: "",
      startDate: "",
    },
  });
  const { businessBranchesDataMap, allChannelsDefDataMap } = useSelector(
    (state: RootState) => ({
      businessBranchesDataMap: state.businessReducer.businessBranchesDataMap,
      allChannelsDefDataMap: state.businessReducer.allChannelsDefDataMap,
    })
  );
  const DROPDOWN_CONFIGS = {
    branchId: {
      optionsMap: businessBranchesDataMap,
      placeholder: "Select Branch",
      uniqueKey: "branchId",
      dropdownHeading: "Branches",
      searchPlaceholder: "Search a branch",
      parentClass: "w-1/5 max-w-[170px] ml-[12px]",
      key: "branchId",
      getListItemText: (data: any) => data?.branchName,
    },
    channelDefId: {
      optionsMap: allChannelsDefDataMap,
      placeholder: "Select Channel",
      uniqueKey: "channelDefId",
      dropdownHeading: "Channels",
      searchPlaceholder: "Search a channel",
      parentClass: "w-1/5 max-w-[170px] ml-[12px]",
      key: "channelDefId",
      getListItemText: (data: any) => data?.name,
    },
  };

  const handleFilterChange = (value: any, fieldToUpdate: string) => {
    let dataToUpdate = cloneDeep(filterConfig);
    set(dataToUpdate, fieldToUpdate, value);
    setFilterConfig(dataToUpdate);
  };

  const onDateRangeSubmission = (data: {
    startDate: Moment;
    endDate: Moment;
  }) => {
    let dateRangeValue = {
      startDate: convertMomentToString(
        data.startDate,
        DATE_TIME_FORMATS.dayMonthYear
      ),
      endDate: convertMomentToString(
        data.endDate,
        DATE_TIME_FORMATS.dayMonthYear
      ),
    };
    handleFilterChange(dateRangeValue, "dateRange");
  };

  /**
   * The dates more than 3 years will always be disabled
   * @param current
   */
  const disabledDate = (current: any) => {
    return (
      current &&
      (current >= moment().endOf("day") || moment().diff(current, "days") > 364)
    );
  };

  const isLargeSizeDevice = () => {
    return window.matchMedia("only screen and (min-width: 1321px)").matches;
  };

  function renderCustomDropDown(dropdownConfig: {
    optionsMap: any;
    placeholder?: string;
    getListItemText?: any;
    parentClass?: string;
    dropdownHeading?: string;
    searchPlaceholder?: string;
    uniqueKey: any;
    key: string;
  }): any {
    return (
      <CustomDropdown
        optionsMap={dropdownConfig.optionsMap}
        isMultiSelect={false}
        placeholder={dropdownConfig.placeholder}
        getListItemText={dropdownConfig.getListItemText}
        parentClass={dropdownConfig.parentClass}
        dropdownHeading={dropdownConfig.dropdownHeading}
        searchPlaceholder={dropdownConfig.searchPlaceholder}
        onSubmit={(value: any) =>
          handleFilterChange(value[0], dropdownConfig.key)
        }
        preSelectedValues={filterConfig[dropdownConfig.key]}
        uniqueKey={dropdownConfig.uniqueKey}
        showErrorBox={false}
      ></CustomDropdown>
    );
  }

  function renderAggregatorCheckbox(config: {
    id: string;
    label: string;
    parentClass: string;
  }) {
    const { id, label, parentClass } = config;
    let isSelected = filterConfig?.aggregatorType === id;
    return (
      <div
        className={`flex items-center border rounded-[8px] px-18 py-[9px] cursor-pointer
          ${parentClass || ""}
          ${isSelected ? "border-[#2A77F2]" : "border-[#DEDEDF]"}
        `}
        onClick={() => handleFilterChange(id, "aggregatorType")}
      >
        <Checkbox
          checked={isSelected}
          size="small"
          inputProps={{ "aria-label": "controlled" }}
          className="!m-0 !p-0"
        />
        <p className="text-14 font-medium leading-none mx-10">{label}</p>
        <img src={getAggregatorLogo(id)} alt="" width={20} height={20} />
      </div>
    );
  }

  function renderApplyAndUploadCtas() {
    let enableApplyCta =
      filterConfig?.branchId &&
      filterConfig?.channelDefId &&
      filterConfig?.aggregatorType &&
      filterConfig?.dateRange?.startDate &&
      filterConfig?.dateRange?.endDate;
    return isLargeSizeDevice() ? (
      <>
        <CtaComponent
          ctaText="Apply"
          ctaSize={CTA_SIZE.small}
          ctaType={CTA_TYPE.active_cta}
          onCtaClick={() => onApplyCtaClick(filterConfig)}
          customClass={"ml-12"}
          isDisabled={!enableApplyCta}
        />
        <CtaComponent
          ctaText="Upload New File"
          ctaSize={CTA_SIZE.small}
          ctaType={CTA_TYPE.secondary_cta}
          onCtaClick={() => setIsFileUploadModalOpen(true)}
          iconClass="ic-upload"
          customClass={"text-[#2A77F2] border-[#2A77F2] absolute right-20"}
        />
      </>
    ) : (
      <>
        <div
          className={`rista-sprite ic-tick-blue ml-12 ${
            enableApplyCta ? "cursor-pointer" : "cursor-not-allowed opacity-70"
          }`}
          onClick={() => enableApplyCta && onApplyCtaClick(filterConfig)}
          title="Apply"
        ></div>
        <div
          className="rista-sprite ic-upload cursor-pointer scale-125 absolute right-20"
          onClick={() => setIsFileUploadModalOpen(true)}
          title="Upload New File"
        ></div>
      </>
    );
  }

  return (
    <div className="top-action-bar flex items-center p-20 pr-44 bg-white relative">
      {renderAggregatorCheckbox({
        ...AGGREGATOR_TYPE.swiggy,
        parentClass: "mr-6",
      })}
      {renderAggregatorCheckbox({
        ...AGGREGATOR_TYPE.zomato,
        parentClass: "ml-6",
      })}
      {renderCustomDropDown(DROPDOWN_CONFIGS.branchId)}
      {renderCustomDropDown(DROPDOWN_CONFIGS.channelDefId)}
      <DateRangePicker
        dateFormat={DATE_TIME_FORMATS.dayMonthYear}
        disabledDate={disabledDate}
        timeDifference={365}
        errorMessage={`You can select date range max upto 90 days!`}
        onSubmit={onDateRangeSubmission}
        defaultDateRange={[null, null]}
      ></DateRangePicker>
      {renderApplyAndUploadCtas()}
    </div>
  );
};

export default TopActionBar;
